import isEqual from 'lodash-es/isEqual'
import { type ReactNode, useCallback, useRef, useState } from 'react'

import { ActionsContextProvider, type ActionsOverwrite } from './actionsContext'

type Props = {
  children: ReactNode
}

const ActionsProvider = ({ children }: Props) => {
  const [actions, setActions] = useState(new Map<string, ActionsOverwrite>())
  const [currentId, setCurrentId] = useState<string>()
  const actionsRef = useRef(actions)

  const handleRegister = useCallback((action: ActionsOverwrite) => {
    setActions((currentActions) => {
      const previousAction = currentActions.get(action.key)
      if (previousAction && isEqual(previousAction, action)) {
        return currentActions
      }

      actionsRef.current = new Map(currentActions)
      actionsRef.current.set(action.key, action)

      return actionsRef.current
    })
  }, [])

  const handleUnregister = useCallback((actionKey: string) => {
    setActions((currentActions) => {
      if (!currentActions.has(actionKey)) {
        return currentActions
      }

      actionsRef.current = new Map(currentActions)
      actionsRef.current.delete(actionKey)

      return actionsRef.current
    })
  }, [])

  const handleOpen = useCallback((actionKey: string) => {
    setCurrentId(actionKey)
  }, [])

  const handleOpenByActionId = useCallback((actionId: string) => {
    for (const [actionKey, action] of actionsRef.current) {
      if (action.isVisible && action.actionId === actionId) {
        setCurrentId(actionKey)
        return
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    if (!currentId) {
      throw new Error('Cannot close action. No action is currently in scope.')
    }

    setCurrentId(undefined)
  }, [currentId])

  return (
    <ActionsContextProvider
      value={{
        actions,
        onRegister: handleRegister,
        onUnregister: handleUnregister,
        onOpen: handleOpen,
        onOpenCreateCommandData: handleOpenByActionId,
        onClose: handleClose,
        currentId,
      }}
    >
      {children}
    </ActionsContextProvider>
  )
}

export { ActionsProvider }
